// Columna izquierda del explorer (`.col-filters` del mock): Búsqueda · Canal · Fecha · Monto.
import { cn } from '@/lib/cn';
import { FiltersColumn } from '@/shared/patterns/FiltersColumn';
import {
    PAYMENT_CHANNEL_OPTIONS,
    PAYMENT_DATE_SHORTCUTS,
    countActiveFilters,
    dateShortcutRange,
} from '../logic';
import { emptyPaymentFilters, type PaymentFilters } from '../types';

const LABEL = 'mb-[5px] text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground';
const GROUP = 'mt-2.5';
const FIELD =
    'h-[26px] w-full rounded border border-border bg-card px-2 text-[11.5px] text-foreground outline-none focus-visible:border-ring';

export interface PaymentsFiltersPanelProps {
    filters: PaymentFilters;
    onChange: (next: PaymentFilters) => void;
    /** El día contra el que corren los atajos Hoy · 7d · 30d. */
    today: string;
    collapsed: boolean;
    onToggleCollapsed: () => void;
}

export function PaymentsFiltersPanel({
    filters,
    onChange,
    today,
    collapsed,
    onToggleCollapsed,
}: PaymentsFiltersPanelProps) {
    const patch = (partial: Partial<PaymentFilters>) => {
        onChange({ ...filters, ...partial });
    };

    // Vacío es «sin tope», no cero: un mínimo en cero dejaría fuera los pagos negativos que no hay.
    const amountOf = (raw: string): number | null => (raw.trim() === '' ? null : Number(raw));

    return (
        <FiltersColumn
            collapsed={collapsed}
            onToggleCollapsed={onToggleCollapsed}
            activeCount={countActiveFilters(filters)}
            onReset={() => {
                onChange(emptyPaymentFilters());
            }}
            regionLabel="Filtros de pagos"
        >
            <div className={GROUP}>
                <div className={LABEL}>Búsqueda</div>
                <input
                    type="search"
                    value={filters.search}
                    placeholder="Cliente, PG-XXXX o CR-XXXX"
                    aria-label="Cliente, PG-XXXX o CR-XXXX"
                    onChange={(event) => {
                        patch({ search: event.target.value });
                    }}
                    className={FIELD}
                />
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Canal</div>
                <select
                    value={filters.channel}
                    aria-label="Canal"
                    onChange={(event) => {
                        patch({ channel: event.target.value as PaymentFilters['channel'] });
                    }}
                    className={FIELD}
                >
                    {PAYMENT_CHANNEL_OPTIONS.map((option) => (
                        <option key={option.label} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Fecha</div>
                <div className="grid grid-cols-2 gap-1">
                    <input
                        type="date"
                        value={filters.fromDay}
                        aria-label="Recibido desde"
                        onChange={(event) => {
                            patch({ fromDay: event.target.value });
                        }}
                        className={cn(FIELD, 'tabular-nums')}
                    />
                    <input
                        type="date"
                        value={filters.toDay}
                        aria-label="Recibido hasta"
                        onChange={(event) => {
                            patch({ toDay: event.target.value });
                        }}
                        className={cn(FIELD, 'tabular-nums')}
                    />
                </div>
                <div className="mt-1 grid grid-cols-4 gap-1">
                    {PAYMENT_DATE_SHORTCUTS.map((shortcut) => (
                        <button
                            key={shortcut.label}
                            type="button"
                            onClick={() => {
                                patch(dateShortcutRange(shortcut.days, today));
                            }}
                            className="rounded border border-border px-1 py-[2px] text-[10px] text-muted-foreground transition-colors hover:text-foreground"
                        >
                            {shortcut.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Monto</div>
                <div className="grid grid-cols-2 gap-1">
                    <input
                        type="number"
                        min={0}
                        inputMode="numeric"
                        value={filters.amountMin === null ? '' : String(filters.amountMin)}
                        placeholder="Mín"
                        aria-label="Monto mínimo"
                        onChange={(event) => {
                            patch({ amountMin: amountOf(event.target.value) });
                        }}
                        className={cn(FIELD, 'tabular-nums')}
                    />
                    <input
                        type="number"
                        min={0}
                        inputMode="numeric"
                        value={filters.amountMax === null ? '' : String(filters.amountMax)}
                        placeholder="Máx"
                        aria-label="Monto máximo"
                        onChange={(event) => {
                            patch({ amountMax: amountOf(event.target.value) });
                        }}
                        className={cn(FIELD, 'tabular-nums')}
                    />
                </div>
            </div>
        </FiltersColumn>
    );
}
